import { useForm } from "react-hook-form";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";

import { datadogLogs } from "@datadog/browser-logs";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import WordProcessorField from "components/word-processor-field";
import { useUpdateAmbientScribeNoteMutation } from "services/ambient-scribe";

type AfterVisitSummaryEditorProps = {
  /** `AmbientScribeNote.id` of the letter being corrected. */
  noteId: number;
  /** The letter as HTML — pass it through `getAfterVisitSummaryHtml` first. */
  letterHtml: string;
  /** Leave edit mode. Called on Cancel, and on a successful save. */
  onDone: () => void;
  height?: number;
  /** Raise the field's own Templates-style dialogs above a host that lifted its z-index. */
  nestedDialogZIndex?: number;
};

type EditorFormValues = { notes: string };

/**
 * Correct the After Visit Summary letter and save it back to the note row.
 *
 * Shared by the view-and-send dialog and the scribe-details tab for the same reason
 * `use-after-visit-summary-email.hook` is: two surfaces showing the same letter must not drift on
 * what saving it means.
 *
 * **The form is seeded at mount and never re-seeded, and the caller must mount this only while
 * editing.** That is not incidental — it is what protects in-progress typing. `letterHtml` comes
 * from a query that any `AmbientScribeNotes` invalidation refetches, and sending an email anywhere
 * in the app is one such invalidation, so a `reset` keyed on the prop would wipe whatever the user
 * had typed at an arbitrary moment. Mounting per edit session makes mount the only seed.
 *
 * No `featuresGroup`: `getFeatureOptions` takes the group as a required positional argument with no
 * default, so omitting it matches none of the group branches and yields the base feature set — full
 * HTML, lists, alignment, tables, inline marks — with templates and merge tags OFF. That is exactly
 * right for a letter whose values are already resolved. `featuresGroup="light"` (what the scribe
 * context editor uses) would be wrong here: it turns `allowLists` off, and the letter's own
 * "Next Steps" section is a list.
 */
export default function AfterVisitSummaryEditor({
  noteId,
  letterHtml,
  onDone,
  height = 320,
  nestedDialogZIndex
}: Readonly<AfterVisitSummaryEditorProps>) {
  const dispatch = useAppDispatch();
  const [updateNote, { isLoading }] = useUpdateAmbientScribeNoteMutation();

  const { control, handleSubmit, formState } = useForm<EditorFormValues>({
    mode: "onChange",
    defaultValues: { notes: letterHtml }
  });

  const onSubmit = handleSubmit(async ({ notes }) => {
    const response = await updateNote({ id: noteId, notes });

    if ("error" in response) {
      // Stay in edit mode: the user's text lives in this form and nowhere else, so unmounting on
      // failure would discard the very correction they were trying to save.
      dispatch(setCustomMessage(<ErrorSnackbarContent response={response} />));
      dispatch(setOpen(true));
      datadogLogs.logger.error("after visit summary save failed", {
        noteId,
        response
      });
      return;
    }

    datadogLogs.logger.log("after visit summary edited", { noteId });
    onDone();
  });

  return (
    <Stack flex={1} minHeight={0} gap={1.5}>
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", minWidth: 0 }}>
        <WordProcessorField
          name="notes"
          control={control}
          rules={{ required: "The letter cannot be empty" }}
          placeholder="Write the letter the patient should receive…"
          height={height}
          allowToolbarAutohide={false}
          nestedDialogZIndex={nestedDialogZIndex}
          sx={{ flex: 1, width: "100%" }}
        />
      </Box>
      <Stack direction="row" justifyContent="flex-end" gap={1.5}>
        <Button
          variant="outlined"
          onClick={onDone}
          disabled={isLoading}
          data-testid="avs-edit-cancel"
        >
          Cancel
        </Button>
        {/* `loading` AND `disabled`: neither RHF's handleSubmit nor LoadingButton guards re-entry
            on its own, and a double save is a second PATCH over the first one's result. */}
        <LoadingButton
          variant="contained"
          onClick={onSubmit}
          loading={isLoading}
          disabled={isLoading || !formState.isDirty}
          data-testid="avs-edit-save"
        >
          Save
        </LoadingButton>
      </Stack>
    </Stack>
  );
}
